import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, BadgePercent, PackageCheck, Phone } from "lucide-react";
import { scrollToSection } from "@/hooks/useLenis";

const LINES = [
  { text: "SAME-DAY", cls: "text-[#F6F1E3]" },
  { text: "DELIVERY,", cls: "text-outline-gold" },
  { text: "IN A KILT.", cls: "text-[#F0A829]" },
];

const STATS = [
  { k: "01", v: "Same-day or next-day" },
  { k: "02", v: "Free tracking & POD" },
  { k: "03", v: "$5 off your 2nd order" },
  { k: "04", v: "4 towns, 1 kilt" },
];

const lineReveal = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="grain relative overflow-hidden">
      <div className="tartan-bg absolute inset-0 opacity-[0.16]" />
      <div className="absolute inset-0 bg-[radial-gradient(1100px_520px_at_78%_18%,rgba(240,168,41,0.14),transparent_60%),radial-gradient(900px_600px_at_8%_85%,rgba(30,158,90,0.16),transparent_60%)]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 pt-44 sm:px-8 min-[820px]:pt-32 lg:grid-cols-12 lg:gap-8 lg:pb-28 lg:pt-40">
        <motion.div style={{ y: yText, opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-mono-brand text-xs tracking-[0.35em] uppercase text-[#F0A829]"
            data-testid="hero-eyebrow"
          >
            Canberra → Cooma · Berridale · Jindabyne
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-display mt-6 text-6xl font-extrabold uppercase leading-[0.92] tracking-tight sm:text-8xl lg:text-[7.5rem]"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  className={`block ${line.cls}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#B9C4B4] sm:text-lg"
            data-testid="hero-subcopy"
          >
            Click &amp; collect delivery from Canberra up the mountain to Cooma,
            Berridale and Jindabyne. Order and pay in the app — every job comes
            with free tracking and proof of delivery.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-call-button"
              href="tel:+61408464106"
              className="group flex items-center gap-3 rounded-full bg-[#F0A829] px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-[#0C1F17] transition-transform duration-300 hover:-translate-y-1"
            >
              <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" strokeWidth={2.5} />
              Call 0408 464 106
            </a>
            <button
              data-testid="hero-services-button"
              onClick={() => scrollToSection("#services")}
              className="group flex items-center gap-3 rounded-full border border-[#F6F1E3]/25 px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-[#F6F1E3] transition-colors duration-300 hover:border-[#F0A829] hover:text-[#F0A829]"
            >
              Explore services
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" strokeWidth={2.5} />
            </button>
          </motion.div>

          <motion.a
            data-testid="hero-google-badge"
            href="https://maps.app.goo.gl/jzYk1XyE1DzvGwFo6"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="group mt-5 inline-flex items-center gap-2.5 rounded-full border border-[#F0A829]/40 bg-[#122A1F]/70 px-4 py-2.5 backdrop-blur-sm transition-colors duration-300 hover:border-[#F0A829]"
          >
            <span className="flex gap-0.5" aria-label="5 out of 5 stars on Google">
              {[0, 1, 2, 3, 4].map((s) => (
                <svg key={s} viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-[#F0A829]">
                  <path d="M12 2l2.9 6.6 7.1.7-5.4 4.8 1.6 7-6.2-3.8L5.8 21l1.6-7L2 9.3l7.1-.7z" />
                </svg>
              ))}
            </span>
            <span className="font-display text-sm font-bold uppercase tracking-wide text-[#F6F1E3]">
              5.0 on Google
            </span>
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#B9C4B4] group-hover:text-[#F0A829]">
              6 reviews
            </span>
          </motion.a>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.k} className="bg-[#0C1F17]/90 px-4 py-4">
                <p className="font-mono-brand text-[10px] tracking-[0.25em] text-[#F0A829]">{s.k}</p>
                <p className="mt-1.5 font-display text-sm font-semibold uppercase tracking-wide text-[#F6F1E3]">
                  {s.v}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-3xl border border-[#F0A829]/40" />
            <div className="clip-corner relative overflow-hidden rounded-2xl border border-white/10">
              <img
                src="/assets/van.jpg"
                alt="The Kilted Courier van"
                data-testid="hero-van-image"
                className="h-[420px] w-full object-cover sm:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C1F17]/70 via-transparent to-transparent" />
              <p className="font-mono-brand absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.3em] text-[#F6F1E3]/80">
                Unit 01 — The Van
              </p>
            </div>

            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-4 top-8 flex items-center gap-2.5 rounded-2xl border border-[#F0A829]/40 bg-[#122A1F]/90 px-4 py-3 backdrop-blur-md sm:-left-8"
              data-testid="hero-badge-discount"
            >
              <BadgePercent className="h-5 w-5 text-[#F0A829]" strokeWidth={2.2} />
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-wide text-[#F6F1E3]">$5 off</p>
                <p className="text-[11px] text-[#B9C4B4]">your second order</p>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
              className="absolute -right-3 bottom-16 flex items-center gap-2.5 rounded-2xl border border-[#1E9E5A]/50 bg-[#122A1F]/90 px-4 py-3 backdrop-blur-md sm:-right-6"
              data-testid="hero-badge-tracking"
            >
              <PackageCheck className="h-5 w-5 text-[#1E9E5A]" strokeWidth={2.2} />
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-wide text-[#F6F1E3]">Free tracking</p>
                <p className="text-[11px] text-[#B9C4B4]">&amp; proof of delivery</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <div className="tartan-strip relative h-3 w-full opacity-80" />
    </section>
  );
}
